import { useState } from "react";
import { HStack, IconButton, Input, Select, Box } from "@chakra-ui/react";
import { SmallCloseIcon } from "@chakra-ui/icons";
import { useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { HiOutlineLink } from "react-icons/hi";
import { MdFormatColorReset } from "react-icons/md";

// Formatting menu shown over a text selection. It subscribes to just the marks it displays,
// so selection changes re-render this menu rather than the whole editor page.
export default function EditorBubbleMenu({ editor }) {
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);

  const state = useEditorState({
    editor,
    // The selector can run once more against the old editor while it is being rebuilt
    selector: ({ editor }) => (!editor || editor.isDestroyed) ? null : ({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      underline: editor.isActive('underline'),
      strike: editor.isActive('strike'),
      link: editor.isActive('link'),
      fontSize: editor.getAttributes('textStyle').fontSize || '1rem',
    }),
  });

  if (!state) return null;

  return (
    <BubbleMenu
      editor={editor}
      className="editor-bubble-menu"
      appendTo={() => document.body}
      // Positioned with Floating UI; a negative offset overlaps the selection slightly
      options={{ placement: 'top-start', offset: { mainAxis: -10 } }}
      // Only show if there's an actual selection
      shouldShow={({ from, to }) => from !== to}
    >
      <HStack
        bg="white"
        color="gray.800"
        px={2}
        py={1}
        borderRadius="md"
        boxShadow="lg"
        border="1px solid"
        borderColor="gray.200"
        spacing={1}
        onMouseDown={(e) => e.preventDefault()}
      >
        <IconButton
          size="xs"
          icon={<strong>B</strong>}
          onClick={() => editor.chain().focus().toggleBold().run()}
          bg={state.bold ? 'blue.100' : 'transparent'}
          color={state.bold ? 'blue.600' : 'gray.700'}
          _hover={{ bg: state.bold ? 'blue.200' : 'gray.100' }}
          aria-label="Bold"
        />
        <IconButton
          size="xs"
          icon={<em>I</em>}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          bg={state.italic ? 'blue.100' : 'transparent'}
          color={state.italic ? 'blue.600' : 'gray.700'}
          _hover={{ bg: state.italic ? 'blue.200' : 'gray.100' }}
          aria-label="Italic"
        />
        <IconButton
          size="xs"
          icon={<u>U</u>}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          bg={state.underline ? 'blue.100' : 'transparent'}
          color={state.underline ? 'blue.600' : 'gray.700'}
          _hover={{ bg: state.underline ? 'blue.200' : 'gray.100' }}
          aria-label="Underline"
        />
        <IconButton
          size="xs"
          icon={<s>S</s>}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          bg={state.strike ? 'blue.100' : 'transparent'}
          color={state.strike ? 'blue.600' : 'gray.700'}
          _hover={{ bg: state.strike ? 'blue.200' : 'gray.100' }}
          aria-label="Strikethrough"
        />
        <Select
          size="xs"
          w="70px"
          bg="white"
          color="gray.800"
          borderColor="gray.300"
          _hover={{ bg: 'gray.50' }}
          onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
          value={state.fontSize}
        >
          <option value="0.5rem">8</option>
          <option value="0.625rem">10</option>
          <option value="0.75rem">12</option>
          <option value="0.875rem">14</option>
          <option value="1rem">16</option>
          <option value="1.125rem">18</option>
          <option value="1.25rem">20</option>
          <option value="1.5rem">24</option>
          <option value="1.75rem">28</option>
          <option value="2rem">32</option>
        </Select>

        <Box w="1px" h="20px" bg="gray.300" />

        {/* Link Controls */}
        {showLinkInput ? (
          <HStack spacing={1}>
            <Input
              size="xs"
              w="150px"
              bg="white"
              color="gray.800"
              borderColor="gray.300"
              placeholder="Enter URL"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (linkUrl) {
                    editor.chain().focus().setLink({ href: linkUrl }).run();
                  }
                  setShowLinkInput(false);
                  setLinkUrl('');
                }
                if (e.key === 'Escape') {
                  setShowLinkInput(false);
                  setLinkUrl('');
                }
              }}
              autoFocus
            />
            <IconButton
              size="xs"
              icon={<SmallCloseIcon />}
              onClick={() => {
                setShowLinkInput(false);
                setLinkUrl('');
              }}
              bg="transparent"
              color="gray.700"
              _hover={{ bg: 'gray.100' }}
              aria-label="Cancel"
            />
          </HStack>
        ) : (
          <IconButton
            size="xs"
            icon={<HiOutlineLink />}
            onClick={() => {
              if (state.link) {
                editor.chain().focus().unsetLink().run();
              } else {
                const previousUrl = editor.getAttributes('link').href;
                setLinkUrl(previousUrl || '');
                setShowLinkInput(true);
              }
            }}
            bg={state.link ? 'blue.100' : 'transparent'}
            color={state.link ? 'blue.600' : 'gray.700'}
            _hover={{ bg: state.link ? 'blue.200' : 'gray.100' }}
            aria-label="Link"
          />
        )}

        <Box w="1px" h="20px" bg="gray.300" />

        {/* Clear Formatting */}
        <IconButton
          size="xs"
          icon={<MdFormatColorReset />}
          onClick={() => {
            editor.chain().focus().clearNodes().unsetAllMarks().run();
          }}
          bg="transparent"
          color="gray.700"
          _hover={{ bg: 'gray.100' }}
          aria-label="Clear Formatting"
        />
      </HStack>
    </BubbleMenu>
  );
}
